/**
 * @fileoverview Readable names for the PyMOL objects BMaps loads.
 *
 * Inside the viewer every object is `_bm_<kind><n>` (PyMOLConstants.ObjectPrefix): the prefix is
 * what the command line's policy recognizes as "BMaps owns this". That is unreadable to a person,
 * and desktop PyMOL hides names starting with "_" from its object panel altogether - so anything
 * leaving BMaps for a person to look at (an exported session file)
 * describes the objects with these labels instead.
 *
 * Pure: takes the renderer's object map, touches no viewer.
 */

import { ObjectKinds, ObjectPrefix } from './PyMOLConstants';

/** Object kinds holding BMaps model atoms (not surfaces, shapes or temporary molecules). */
export const AtomObjectKinds = [
    ObjectKinds.protein, ObjectKinds.compound, ObjectKinds.water, ObjectKinds.fragment,
    ObjectKinds.hotspot, ObjectKinds.hetero, ObjectKinds.misc,
];

/** Display order of the kinds, and what to call a group of them. */
export const KindOrder = AtomObjectKinds;
export const KindLabels = {
    [ObjectKinds.water]: 'Waters',
    [ObjectKinds.fragment]: 'Fragments',
    [ObjectKinds.hotspot]: 'Hotspots',
    [ObjectKinds.hetero]: 'Other hetero groups',
    [ObjectKinds.misc]: 'Other atoms',
    [ObjectKinds.surface]: 'Surface',
    [ObjectKinds.shapes]: 'Shapes',
    [ObjectKinds.temp]: 'Molecule',
};

/**
 * The loaded BMaps atom objects, in a stable order, with a readable label each.
 * @param {{objects: Map<string, object>}} renderer PyMOLAtomRenderer
 * @returns {{name: string, kind: string, label: string, key: string}[]}
 */
export function describeAtomObjects(renderer) {
    if (!renderer || !renderer.objects) return [];
    const objs = [...renderer.objects.values()]
        .filter((o) => o.loaded && AtomObjectKinds.includes(o.kind) && o.atoms.size > 0);
    // key 'pro:<case>:<model>:<chain>' - more than one protein (case) gets a number
    const caseKeys = [...new Set(objs.filter((o) => o.kind === ObjectKinds.protein)
        .map((o) => o.key.split(':')[1]))];
    const label = (obj) => {
        const first = obj.atoms.values().next().value;
        const atom = first && first.appAtom;
        if (obj.kind === ObjectKinds.protein) {
            const chain = atom && atom.chain ? atom.chain : '';
            const caseNote = caseKeys.length > 1
                ? ` (protein ${caseKeys.indexOf(obj.key.split(':')[1]) + 1})` : '';
            return `${chain ? `Chain ${chain}` : 'Protein'}${caseNote}`;
        }
        if (obj.kind === ObjectKinds.compound) {
            const group = atom && typeof atom.getAtomGroup === 'function' ? atom.getAtomGroup() : null;
            return (group && group.displayName) || 'Compound';
        }
        return KindLabels[obj.kind] || obj.kind;
    };
    return objs
        .map((o) => ({
            name: o.name, kind: o.kind, label: label(o), key: o.key,
        }))
        .sort((a, b) => KindOrder.indexOf(a.kind) - KindOrder.indexOf(b.kind)
            || a.label.localeCompare(b.label, undefined, { numeric: true }));
}

/**
 * A label as a PyMOL object name: word characters only, so it can be typed in a selection
 * ("NUT.201:B" would parse as an atom specification).
 * @param {string} label
 * @returns {string}
 */
export function asObjectName(label) {
    const cleaned = String(label || '')
        .replace(/[^A-Za-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
    if (!cleaned) return 'object';
    return /^[0-9]/.test(cleaned) ? `x${cleaned}` : cleaned;
}

/** `_bm_srf3` -> `Surface_3`, for the objects that are not atoms of the model. */
function labelFromInternalName(name) {
    const rest = name.slice(ObjectPrefix.length);
    const [, kind, seq] = /^([A-Za-z]+)([0-9]*)$/.exec(rest) || [];
    if (!kind) return rest;
    const base = KindLabels[kind] || kind;
    return seq ? `${base} ${seq}` : base;
}

/**
 * `wanted`, or the first of `wanted_2`, `wanted_3` ... that is not taken. PyMOL matches names
 * ignoring case, and giving an existing name to another object or selection (`set_name`,
 * `pseudoatom` ...) replaces or extends the holder, so "taken" ignores case too.
 * @param {string} wanted
 * @param {string[]} taken every name PyMOL knows, objects and selections
 * @returns {string}
 */
export function unusedObjectName(wanted, taken) {
    const lower = new Set(taken.map((n) => String(n).toLowerCase()));
    let unique = wanted;
    for (let n = 2; lower.has(unique.toLowerCase()); n++) unique = `${wanted}_${n}`;
    return unique;
}

/**
 * Names to give BMaps' objects in something a person opens elsewhere (an exported session).
 * Every `_bm_*` object gets one; names already free of the prefix are left alone, and the result
 * collides with nothing PyMOL knows (see unusedObjectName).
 * @param {string[]} names the objects that exist in the viewer
 * @param {{name: string, label: string}[]} described describeAtomObjects() of the same scene
 * @param {string[]} [taken] other names PyMOL knows (selections); `names` are always taken
 * @returns {Map<string, string>} internal name -> exported name (only those that change)
 */
export function exportObjectNames(names, described, taken=[]) {
    const labels = new Map(described.map((o) => [o.name, o.label]));
    const used = [...names, ...taken];
    const renames = new Map();
    for (const name of names) {
        if (!name.startsWith(ObjectPrefix)) continue;
        const wanted = asObjectName(labels.get(name) || labelFromInternalName(name));
        const unique = unusedObjectName(wanted, used);
        used.push(unique);
        renames.set(name, unique);
    }
    return renames;
}
